import { createTranslationHook, type TranslationDictionaries } from '../components/MasterDocument/i18nFactory';

/**
 * Traducciones del modulo Dashboard.
 *
 * ⚠️ Ninguna cadena de este diccionario puede nombrar un producto ni un modulo de negocio: el kit
 * lo consumen Comercial, Financiero y Nomina. Todo lo que sea vocabulario del host —titulo,
 * modulos, categorias— viaja por props, no por aqui. Habia dos que si lo hacian ("Dashboard
 * Comercial" y "Cargando perfil comercial"): se generalizaron, y el titulo paso a ser una prop.
 *
 * <b>Por qué con diccionarios locales y no sólo con el catálogo del host.</b> El motor i18n del kit
 * devuelve la CLAVE CRUDA cuando no encuentra una traducción, así que un host que no haya sembrado
 * `dashboard.*` en su `/langs` vería `dashboard.chrome.loading` en pantalla — peor que el español
 * que había antes de traducir nada. `createTranslationHook` resuelve en cascada y deja el
 * diccionario `es` como último recurso, de modo que **traducir no puede empeorar la pantalla de
 * ningún host**.
 *
 * Es el mismo mecanismo que usan `MasterDocument` y sus paneles, que son los módulos de nivel
 * pantalla del kit. Los dos componentes que usan `react-i18next` directo (`FormCacheSelector`,
 * `LookupField`) son controles pequeños y autocontenidos, otra especie.
 *
 * <b>Precedencia:</b> motor del host en el idioma activo → diccionario local del idioma activo →
 * motor con su fallback → diccionario `es`.
 */
const TRADUCCIONES: TranslationDictionaries = {
  es: {
    // ── Chrome de la pantalla ──
    'dashboard.chrome.loading': 'Cargando tu tablero...',
    'dashboard.chrome.loadError': 'No se pudo cargar el dashboard',
    'dashboard.chrome.retry': 'Reintentar',
    // Agrupa los widgets cuya categoria no declaro el host. Antes era el literal 'Otros',
    // que se pintaba igual en ingles porque nunca pasaba por aqui.
    'dashboard.category.uncategorized': 'Otros',
    'dashboard.chrome.title': 'Tablero de indicadores',
    'dashboard.chrome.viewCascade': 'Vista Cascada',
    'dashboard.chrome.viewTabs': 'Vista Pestañas',
    'dashboard.chrome.viewBoard': 'Tablero único',
    'dashboard.chrome.saving': 'Guardando...',
    'dashboard.chrome.edit': 'Editar',
    'dashboard.chrome.editModeHint': 'Modo edición activo — arrastra y redimensiona los widgets. Cierra el panel para salir.',
    'dashboard.chrome.emptyHint': 'Aún no tienes indicadores en tu tablero. Usa «Editar» para agregarlos desde el catálogo.',
    'dashboard.presentation.enter': 'Modo presentación',
    'dashboard.presentation.exit': 'Salir de presentación',
    'dashboard.presentation.rotate': 'Rotar',

    // ── Selector de compañía ──
    'dashboard.company.placeholder': 'Seleccionar compañía',
    'dashboard.company.search': 'Buscar compañía...',
    'dashboard.company.noMatches': 'Ninguna compañía coincide',
    'dashboard.company.enabled': 'Habilitado',
    'dashboard.company.notEnabled': 'No habilitado',
    'dashboard.company.notAvailable': 'No disponible',
    'dashboard.chrome.empty': 'Dashboard Vacío',

    // ── Acciones sobre un widget ──
    'dashboard.widget.refresh': 'Actualizar datos',
    'dashboard.widget.maximize': 'Maximizar widget',
    'dashboard.widget.minimize': 'Minimizar',
    'dashboard.widget.remove': 'Quitar del dashboard',
    'dashboard.widget.noData': 'Sin datos disponibles',
    'dashboard.widget.unavailable': 'no disponible o archivo no encontrado.',
    'dashboard.widget.justNow': 'justo ahora',
    'dashboard.widget.agoMinutes': 'hace {n} min',
    'dashboard.widget.agoHours': 'hace {n} h',
    'dashboard.widget.highVolume': 'Este widget procesa {count} registros. El rendimiento puede verse afectado.',

    // ── KPI: la referencia del delta se deriva del periodo, nunca se escribe a mano ──
    'dashboard.kpi.vsPreviousDay': 'vs. ayer',
    'dashboard.kpi.vsPreviousWeek': 'vs. semana anterior',
    'dashboard.kpi.vsPreviousMonth': 'vs. mes anterior',
    'dashboard.kpi.trendUp': 'Subió',
    'dashboard.kpi.trendDown': 'Bajó',
    // La etiqueta del cuerpo cuando el KPI agrega varios registros: dice QUÉ se hizo con ellos.
    'dashboard.kpi.aggregatedLabel': '{agg} ({count} registros)',
    'dashboard.kpi.aggSum': 'Total',
    'dashboard.kpi.aggAverage': 'Promedio',
    'dashboard.kpi.aggCount': 'Conteo',
    'dashboard.kpi.aggMin': 'Mínimo',
    'dashboard.kpi.aggMax': 'Máximo',
    'dashboard.kpi.aggLast': 'Último',

    // ── Gráficos ──
    'dashboard.chart.others': 'Otros',
    'dashboard.chart.total': 'Total',

    // ── Tabla ──
    'dashboard.table.rowCount': '{count} registros',
    'dashboard.table.yes': 'Sí',
    'dashboard.table.no': 'No',
    'dashboard.table.expandForMore': 'Ampliar para ver más columnas',

    // ── Confirmaciones ──
    'dashboard.confirm.removeBody': 'Podrás volver a agregarlo desde el catálogo.',
    'dashboard.confirm.removeQuestion': '¿Quitar este widget de tu tablero?',
    'dashboard.confirm.removeAction': 'Quitar',
    'dashboard.confirm.deleteTitle': 'Eliminar widget',
    'dashboard.confirm.deleteBody': 'Esta acción no se puede deshacer.',
    'dashboard.confirm.cancel': 'Cancelar',

    // ── Catálogo ──
    'dashboard.catalog.title': 'Catálogo de Indicadores',
    'dashboard.catalog.search': 'Buscar indicador...',
    'dashboard.catalog.empty': 'No se encontraron indicadores.',
    'dashboard.catalog.deleteWidget': 'Eliminar widget del catálogo',

    // ── Diálogo de acceso por widget (F5-bis) ──
    //
    // ⚠️ Ninguna de estas cadenas nombra un módulo de negocio ni un permiso concreto: los códigos de
    // los nombres de los módulos son vocabulario del HOST y viajan por el adaptador.
    'dashboard.builder.open': 'Crear un indicador',
    'dashboard.builder.openExternal': 'Crear un indicador o conectar un API externo',
    // 🔴 Marco POSITIVO en toda la ventana: el verbo es siempre «ver» y el sujeto siempre
    // «indicador». Ni «restringir», ni «cerrar», ni «widget» — tres palabras que decian lo mismo de
    // tres maneras, y dos de ellas en negativo.
    'dashboard.access.dialogTitle': 'Quién ve este indicador',
    'dashboard.access.close': 'Cerrar',
    'dashboard.access.searchPeople': 'Buscar una persona por nombre o correo...',
    'dashboard.access.pickModule': 'Elegir un módulo...',
    'dashboard.access.searchPermissions': 'Buscar un permiso por nombre o código...',
    'dashboard.access.noPermissionMatch': 'Ningún permiso tuyo coincide. Solo puedes conceder por un permiso que tú tengas.',
    'dashboard.access.permissionOwnScope': 'Solo aparecen los permisos que tú tienes: no se puede conceder por uno ajeno.',
    'dashboard.access.subjectIdPlaceholder': 'Identificador de quien recibe el acceso',
    'dashboard.access.subject.user': 'Una persona',
    'dashboard.access.subject.permission': 'Un permiso',
    'dashboard.access.subject.module': 'Un módulo',
    'dashboard.access.level': 'Nivel',
    'dashboard.access.level.viewer': 'Puede ver',
    'dashboard.access.level.editor': 'Puede editar',
    'dashboard.access.level.owner': 'Puede administrar',
    'dashboard.access.expiry': 'Vencimiento',
    'dashboard.access.expiryDefault': 'Con el vencimiento por defecto',
    'dashboard.access.expiryDefaultDays': 'Vence en {days} días',
    'dashboard.access.expiryDate': 'Vence en una fecha',
    'dashboard.access.expiryNever': 'Sin vencimiento',
    'dashboard.access.expiresOn': 'Vence el {date}',
    'dashboard.access.grant': 'Dar acceso',
    'dashboard.access.currentAccess': 'Con acceso',
    'dashboard.access.noneYet': 'Nadie más tiene acceso a este indicador.',
    'dashboard.access.revoke': 'Quitar el acceso',
    'dashboard.access.loading': 'Cargando...',
    'dashboard.access.inert': 'sin efecto',
    'dashboard.access.grantInert': 'El acceso quedó guardado, pero a esta persona NO le va a aparecer: le falta un permiso de más arriba. Concédeselo donde se administran los permisos — no le quites el requisito al indicador.',
    'dashboard.access.visibility': 'Quién lo alcanza',
    'dashboard.access.needsPublish': 'Abrirlo a un módulo entero requiere el permiso de publicar.',
    // ── 🔴 F5 · UNA sola pregunta: ¿quién ve este indicador? ──
    //
    // Hubo una segunda —la sensibilidad— y se retiró el 2026-09-14, de la pantalla y del modelo.
    // Se parecía a ésta («las dos quitan gente») y se comportaba al revés, y la única señal de esa
    // diferencia eran tres palabras en una columna.
    // La casilla. Quitados los radios, lo que queda es binario — y un binario se dice con un check.
    'dashboard.access.restrictedCheck': 'Limitar quién lo ve',
    'dashboard.access.restrictedCheckOn': 'Elige abajo quién lo ve.',
    // Las dos formas de cerrarlo. Son dos visibilidades distintas del modelo, asi que son
    // EXCLUYENTES — radios, no casillas.
    // 🔴 NEUTRO A PROPOSITO, y este es el sitio donde mas cuesta serlo.
    //
    // Aqui habia dos pares de claves —`scopeOwner`/`scopeAdmins`— y el dialogo elegia entre ellos
    // con `widget.propietario_id`, o sea aplicando una regla del HOST: «privado SIN dueño lo ven los
    // administradores». Eso es `esPersonal` de Comercial, y el kit lo consumen tres productos.
    //
    // Ahora hay UNA clave neutra y el host la corrige por `describeVisibility`. El texto del kit
    // describe el ESTADO y no promete quien entra — que es lo unico que el kit puede afirmar.
    'dashboard.access.scopeClosed': 'Cerrado',
    'dashboard.access.scopeClosedHint': 'No aparece para nadie por esta vía.',
    'dashboard.access.scopeUsers': 'Personas específicas',
    'dashboard.access.scopeUsersHint': 'Solo quienes figuren en la lista.',
    // ⚠️ Aqui estaba `adminsAlways`: «quienes administran {vertical} lo ven siempre». Se retiro del
    // diccionario a proposito — no es copy, es una AFIRMACION sobre el modelo del host. La escribe
    // el host por `describeAlwaysSee`, y sin respuesta la nota no se pinta.
    'dashboard.access.noChanges': 'Sin cambios',
    // El motor pluraliza por SUFIJO y con `count` numerico. Sin `_other`, «2 cambios» caia a la
    // clave base y se leia igual que el singular.
    'dashboard.access.pendingChanges_one': '1 cambio sin guardar',
    'dashboard.access.pendingChanges_other': '{count} cambios sin guardar',
    'dashboard.access.pickSomeoneFirst': 'Elige primero a una persona de la lista.',
    'dashboard.access.cancel': 'Cancelar',
    'dashboard.access.saveChanges': 'Guardar cambios',
    // 🔴 Se pinta en el TOAST, no en la ventana: la ventana ya se cerró cuando esto se lee. Neutro a
    // proposito —«el acceso», no «la vertical»— porque el kit no sabe a quien alcanza el indicador.
    'dashboard.access.savedOk': 'Se guardó quién ve este indicador.',
    'dashboard.access.saveFailedSome':
      'Se guardó lo demás, pero esto quedó sin aplicar: {quienes}. Vuelve a intentarlo.',
    // 🔴 Sin la palabra «vertical», que es vocabulario de Comercial —y este diccionario tiene
    // prohibido nombrar un modulo de negocio, lo dice su propio encabezado—. `{alcance}` lo llena el
    // host con la etiqueta que use: «Ventas», «Tesorería», «Nómina».
    'dashboard.access.restrictedCheckOff': 'Sin marcar, lo ven todos los que alcanza el indicador.',
    'dashboard.access.restrictedCheckOffNamed': 'Sin marcar, lo ve todo {alcance}.',
    'dashboard.access.restrictedNeedsPublish':
      'Para quitar la restricción hace falta poder abrir el indicador a un colectivo, que es otro acto.',
    'dashboard.access.audienceToday': 'Hoy lo ve:',
    'dashboard.access.add': 'Añadir',
    'dashboard.access.save': 'Guardar',
    'dashboard.access.saving': 'Guardando...',

    // ── Personalización ──
    'dashboard.customize.visualization': 'Visualización',
    'dashboard.customize.name': 'Nombre del Widget',
    'dashboard.customize.category': 'Categoría',
    'dashboard.customize.width': 'Ancho por defecto',
    'dashboard.customize.widthSmall': 'Pequeño (3 cols)',
    'dashboard.customize.widthMedium': 'Mediano (6 cols)',
    'dashboard.customize.widthFull': 'Ancho Total (12 cols)',
    'dashboard.customize.refresh': 'Frecuencia de actualización',

    // ── Mapeo de campos ──
    'dashboard.fields.chartFields': 'Campos del gráfico',
    'dashboard.fields.groupBy': 'Agrupar por',
    'dashboard.fields.available': 'Campos disponibles del endpoint',

    // -- Widget Builder --
    'dashboard.builder.step1.title': 'Conexión a Datos',
    'dashboard.builder.step1.subtitle': 'Selecciona un indicador del catálogo o conecta tu propia API.',
    'dashboard.builder.step1.catalog': 'Catálogo de indicadores',
    'dashboard.builder.step1.manual': 'URL manual / Siesa',
    'dashboard.builder.step1.ready': 'Listo para continuar',
    'dashboard.builder.step1.external': 'Servicio Externo — las credenciales del usuario no se propagan a esta API.',
    'dashboard.builder.step1.method': 'Método',
    'dashboard.builder.step1.url': 'URL del Endpoint',
    'dashboard.builder.step1.headersTitle': 'Headers y Body',
    'dashboard.builder.step1.customHeaders': 'Headers personalizados',
    'dashboard.builder.step1.error': 'Error:',
    'dashboard.builder.step1.performance': 'Rendimiento:',

    'dashboard.builder.step2.title': 'Mapeo de Datos',
    'dashboard.builder.step2.subtitle': 'Indica cómo se relacionan los campos del JSON con la visualización seleccionada.',
    'dashboard.builder.step2.noData': 'No hay datos disponibles. Vuelve al paso anterior y prueba la conexión primero.',
    'dashboard.builder.step2.aggregation': 'Tipo de agregación',
    'dashboard.builder.step2.axisXLabels': 'Eje X — Etiquetas',
    'dashboard.builder.step2.axisXTime': 'Eje X — Etiquetas / Tiempo',
    'dashboard.builder.step2.axisYValues': 'Eje Y — Valores',
    'dashboard.builder.step2.categoryField': 'Campo de categorías',
    'dashboard.builder.step2.valuesField': 'Campo de valores',
    'dashboard.builder.step2.availableColumns': 'Columnas disponibles',
    'dashboard.builder.step2.allAssigned': 'Todas asignadas',
    'dashboard.builder.step2.selectedColumns': 'Columnas seleccionadas (orden)',
    'dashboard.builder.step2.dragHint': 'Arrastra o haz click para agregar',
    'dashboard.builder.step2.direction': 'Dirección',
    'dashboard.builder.step2.asc': 'Ascendente',
    'dashboard.builder.step2.desc': 'Descendente',
    'dashboard.builder.step2.autoMapping': 'Mapeo automático detectado',

    'dashboard.builder.step3.title': 'Tipo de Presentación',
    'dashboard.builder.step3.subtitle': 'Elige cómo se va a visualizar el indicador en el dashboard.',

    'dashboard.builder.step4.title': 'Apariencia y Dimensiones',
    'dashboard.builder.step4.description': 'Descripción corta (opcional)',
    'dashboard.builder.step4.preview': 'Vista previa',
    'dashboard.builder.step4.noRefresh': 'Sin auto-refresh',
    'dashboard.builder.step4.every30s': 'Cada 30 segundos',
    'dashboard.builder.step4.every1m': 'Cada 1 minuto',
    'dashboard.builder.step4.every2m': 'Cada 2 minutos',
    'dashboard.builder.step4.every5m': 'Cada 5 minutos',
    'dashboard.builder.step4.every10m': 'Cada 10 minutos',
    'dashboard.builder.step4.every30m': 'Cada 30 minutos',

    'dashboard.builder.step5.title': 'Publicación y Permisos',
    'dashboard.builder.step5.private': 'Widget Privado (Solo yo)',
    'dashboard.builder.step5.privateHint': 'Solo tú podrás ver este widget en tu catálogo personal.',
    'dashboard.builder.step5.public': 'Widget Público (Corporativo)',
    'dashboard.builder.step2.valueFieldLabel': 'Campo de valor',
    'dashboard.builder.step2.detectedValue': 'Campo valor:',
    'dashboard.builder.step2.detectedDate': 'Campo fecha:',
    'dashboard.builder.step1.searchIndicator': 'Buscar indicador...',
    'dashboard.builder.step4.namePlaceholder': 'Ej. Tasa de Conversión B2B',
    // 🔴 F3 — el rotulo dice la VERDAD. «Publico» a secas sugiere «todo el mundo», y nunca fue
    // cierto: siempre fue «todo el mundo DE ESTE GRUPO». Se conserva la clave `public` porque es lo
    // que se pinta cuando la categoria no declara grupo, que es un estado distinto.
    //
    // ⚠️ SIN la palabra «vertical», que es vocabulario de Comercial (`DashboardVertical` de Core) y
    // este diccionario tiene prohibido nombrar un modulo de negocio —lo dice su propio encabezado—.
    // `{alcance}` lo llena el host con SU etiqueta: «Ventas», «Tesoreria», «Nomina».
    'dashboard.builder.step5.publicIn': 'Público en {alcance}',
    'dashboard.builder.step5.publicInHint':
      'Sale de la categoría «{alcance}»: no se pregunta aparte, y así no pueden discrepar.',
    'dashboard.builder.step5.noScopeHint':
      'Esta categoría no declara a qué grupo pertenece, así que no hay dónde publicar. Elige otra categoría en el paso anterior.',
    'dashboard.builder.step5.toEveryone': 'A todos los de {alcance}',
    'dashboard.builder.step5.toPeople': 'Solo a estas personas',
    'dashboard.builder.step5.noSearch':
      'Este host no tiene buscador de personas. Se puede repartir después, desde el diálogo de acceso.',
    'dashboard.builder.step5.searchPerson': 'Buscar persona…',
    'dashboard.builder.step5.removePerson': 'Quitar a {persona}',
    'dashboard.builder.step5.noPeopleYet':
      'Sin nadie en la lista, el indicador se crea restringido y sin acceso para nadie — ni siquiera para ti. Agrega al menos una persona, o elige «A todos los de {alcance}».',
    'dashboard.builder.step5.ceilingWarning':
      'Solo alcanza a quien ya tenga {alcance}: el acceso se guarda igual para los demás, y queda sin efecto.',
    // 🔴 F4 — las tres llamadas no tienen transaccion, asi que el final a medias TIENE NOMBRE. El
    // peor caso es un indicador creado, restringido y sin nadie dentro: invisible hasta para su
    // autor, y sin ningun error que lo delate.
    'dashboard.builder.step5.partialNobody':
      'El indicador «{nombre}» se creó y quedó restringido, pero NO se pudo dar acceso a nadie: ahora mismo no lo ve nadie, ni tú. Ábrelo en el diálogo de acceso para repartirlo.',
    'dashboard.builder.step5.partialSome':
      'El indicador «{nombre}» se creó y quedó restringido. Entraron: {ok}. NO entraron: {fallidos}. Ábrelo en el diálogo de acceso para completar el reparto.',
    'dashboard.builder.step5.partialNotRestricted':
      'El indicador «{nombre}» se creó, pero NO se pudo restringir, así que sigue privado para ti y nadie más lo ve. Ábrelo en el diálogo de acceso para repartirlo.',
    'dashboard.builder.step5.openAccessDialog': 'Abrir el diálogo de acceso',
    'dashboard.builder.step5.dismissPartial': 'Entendido, lo dejo así por ahora',
    'dashboard.widget.emptyData': 'Sin datos',
    'dashboard.widget.imageUnavailable': 'Imagen no disponible',
  },

  en: {
    'dashboard.chrome.loading': 'Loading your dashboard...',
    'dashboard.chrome.loadError': 'The dashboard could not be loaded',
    'dashboard.chrome.retry': 'Retry',
    'dashboard.category.uncategorized': 'Other',
    'dashboard.chrome.title': 'KPI dashboard',
    'dashboard.chrome.viewCascade': 'Cascade view',
    'dashboard.chrome.viewTabs': 'Tab view',
    'dashboard.chrome.viewBoard': 'Single board',
    'dashboard.chrome.saving': 'Saving...',
    'dashboard.chrome.edit': 'Edit',
    'dashboard.chrome.editModeHint': 'Edit mode on — drag and resize the widgets. Close the panel to exit.',
    'dashboard.chrome.emptyHint': 'You have no indicators on your dashboard yet. Use "Edit" to add them from the catalog.',
    'dashboard.presentation.enter': 'Presentation mode',
    'dashboard.presentation.exit': 'Exit presentation',
    'dashboard.presentation.rotate': 'Rotate',

    'dashboard.company.placeholder': 'Select company',
    'dashboard.company.search': 'Search company...',
    'dashboard.company.noMatches': 'No company matches',
    'dashboard.company.enabled': 'Enabled',
    'dashboard.company.notEnabled': 'Not enabled',
    'dashboard.company.notAvailable': 'Not available',
    'dashboard.chrome.empty': 'Empty dashboard',

    'dashboard.widget.refresh': 'Refresh data',
    'dashboard.widget.maximize': 'Maximize widget',
    'dashboard.widget.minimize': 'Minimize',
    'dashboard.widget.remove': 'Remove from dashboard',
    'dashboard.widget.noData': 'No data available',
    'dashboard.widget.unavailable': 'unavailable or file not found.',
    'dashboard.widget.justNow': 'just now',
    'dashboard.widget.agoMinutes': '{n} min ago',
    'dashboard.widget.agoHours': '{n} h ago',
    'dashboard.widget.highVolume': 'This widget processes {count} records. Performance may be affected.',

    'dashboard.kpi.vsPreviousDay': 'vs. yesterday',
    'dashboard.kpi.vsPreviousWeek': 'vs. previous week',
    'dashboard.kpi.vsPreviousMonth': 'vs. previous month',
    'dashboard.kpi.trendUp': 'Up',
    'dashboard.kpi.trendDown': 'Down',
    'dashboard.kpi.aggregatedLabel': '{agg} ({count} records)',
    'dashboard.kpi.aggSum': 'Total',
    'dashboard.kpi.aggAverage': 'Average',
    'dashboard.kpi.aggCount': 'Count',
    'dashboard.kpi.aggMin': 'Minimum',
    'dashboard.kpi.aggMax': 'Maximum',
    'dashboard.kpi.aggLast': 'Latest',

    'dashboard.chart.others': 'Other',
    'dashboard.chart.total': 'Total',

    'dashboard.table.rowCount': '{count} records',
    'dashboard.table.yes': 'Yes',
    'dashboard.table.no': 'No',
    'dashboard.table.expandForMore': 'Expand to see more columns',

    'dashboard.confirm.removeBody': 'You can add it back from the catalog.',
    'dashboard.confirm.removeQuestion': 'Remove this widget from your dashboard?',
    'dashboard.confirm.removeAction': 'Remove',
    'dashboard.confirm.deleteTitle': 'Delete widget',
    'dashboard.confirm.deleteBody': 'This cannot be undone.',
    'dashboard.confirm.cancel': 'Cancel',

    'dashboard.catalog.title': 'Indicator catalog',
    'dashboard.catalog.search': 'Search indicator...',
    'dashboard.catalog.empty': 'No indicators found.',
    'dashboard.catalog.deleteWidget': 'Delete widget from catalog',

    // ── Widget access dialog (F5-bis) ──
    'dashboard.builder.open': 'Create an indicator',
    'dashboard.builder.openExternal': 'Create an indicator or connect an external API',
    'dashboard.access.dialogTitle': 'Who sees this widget',
    'dashboard.access.close': 'Close',
    'dashboard.access.searchPeople': 'Search a person by name or email...',
    'dashboard.access.pickModule': 'Pick a module...',
    'dashboard.access.searchPermissions': 'Search a permission by name or code...',
    'dashboard.access.noPermissionMatch': 'None of your permissions match. You can only grant through a permission you hold.',
    'dashboard.access.permissionOwnScope': "Only permissions you hold are listed: you cannot grant through someone else's.",
    'dashboard.access.subjectIdPlaceholder': 'Identifier of the access recipient',
    'dashboard.access.subject.user': 'A person',
    'dashboard.access.subject.permission': 'A permission',
    'dashboard.access.subject.module': 'A module',
    'dashboard.access.level': 'Level',
    'dashboard.access.level.viewer': 'Can view',
    'dashboard.access.level.editor': 'Can edit',
    'dashboard.access.level.owner': 'Can manage',
    'dashboard.access.expiry': 'Expiry',
    'dashboard.access.expiryDefault': 'With the default expiry',
    'dashboard.access.expiryDefaultDays': 'Expires in {days} days',
    'dashboard.access.expiryDate': 'Expires on a date',
    'dashboard.access.expiryNever': 'Never expires',
    'dashboard.access.expiresOn': 'Expires on {date}',
    'dashboard.access.grant': 'Give access',
    'dashboard.access.currentAccess': 'With access',
    'dashboard.access.noneYet': 'Nobody else has access to this widget.',
    'dashboard.access.revoke': 'Remove access',
    'dashboard.access.loading': 'Loading...',
    'dashboard.access.inert': 'no effect',
    'dashboard.access.grantInert': 'Access was saved, but this person will NOT see the widget: they are missing a higher-level permission. Grant it where permissions are managed — do not drop the widget requirement.',
    'dashboard.access.visibility': 'Who can reach it',
    'dashboard.access.needsPublish': 'Opening it to a whole module requires the publish permission.',
    'dashboard.access.restrictedCheck': 'Limit who sees it',
    'dashboard.access.restrictedCheckOn': 'Choose below who sees it.',
    'dashboard.access.scopeClosed': 'Closed',
    'dashboard.access.scopeClosedHint': 'It does not show up for anyone through this route.',
    'dashboard.access.scopeUsers': 'Specific people',
    'dashboard.access.scopeUsersHint': 'Only those on the list.',
    'dashboard.access.noChanges': 'No changes',
    'dashboard.access.pendingChanges_one': '1 unsaved change',
    'dashboard.access.pendingChanges_other': '{count} unsaved changes',
    'dashboard.access.pickSomeoneFirst': 'Pick a person from the list first.',
    'dashboard.access.cancel': 'Cancel',
    'dashboard.access.saveChanges': 'Save changes',
    'dashboard.access.savedOk': 'Saved who sees this widget.',
    'dashboard.access.saveFailedSome':
      'Everything else was saved, but this was left unapplied: {quienes}. Try again.',
    'dashboard.access.restrictedCheckOff': 'Unticked, everyone the widget reaches sees it.',
    'dashboard.access.restrictedCheckOffNamed': 'Unticked, all of {alcance} sees it.',
    'dashboard.access.restrictedNeedsPublish':
      'Removing the restriction requires being able to open the widget to a group, which is another act.',
    'dashboard.access.audienceToday': 'Today it is seen by:',
    'dashboard.access.add': 'Add',
    'dashboard.access.save': 'Save',
    'dashboard.access.saving': 'Saving...',

    'dashboard.customize.visualization': 'Visualization',
    'dashboard.customize.name': 'Widget name',
    'dashboard.customize.category': 'Category',
    'dashboard.customize.width': 'Default width',
    'dashboard.customize.widthSmall': 'Small (3 cols)',
    'dashboard.customize.widthMedium': 'Medium (6 cols)',
    'dashboard.customize.widthFull': 'Full width (12 cols)',
    'dashboard.customize.refresh': 'Refresh interval',

    'dashboard.fields.chartFields': 'Chart fields',
    'dashboard.fields.groupBy': 'Group by',
    'dashboard.fields.available': 'Available endpoint fields',

    // -- Widget Builder --
    'dashboard.builder.step1.title': 'Data connection',
    'dashboard.builder.step1.subtitle': 'Pick an indicator from the catalog or connect your own API.',
    'dashboard.builder.step1.catalog': 'Indicator catalog',
    'dashboard.builder.step1.manual': 'Manual URL / Siesa',
    'dashboard.builder.step1.ready': 'Ready to continue',
    'dashboard.builder.step1.external': 'External service - user credentials are not forwarded to this API.',
    'dashboard.builder.step1.method': 'Method',
    'dashboard.builder.step1.url': 'Endpoint URL',
    'dashboard.builder.step1.headersTitle': 'Headers and body',
    'dashboard.builder.step1.customHeaders': 'Custom headers',
    'dashboard.builder.step1.error': 'Error:',
    'dashboard.builder.step1.performance': 'Performance:',

    'dashboard.builder.step2.title': 'Data mapping',
    'dashboard.builder.step2.subtitle': 'Tell us how the JSON fields map to the chosen visualization.',
    'dashboard.builder.step2.noData': 'No data available. Go back and test the connection first.',
    'dashboard.builder.step2.aggregation': 'Aggregation',
    'dashboard.builder.step2.axisXLabels': 'X axis - Labels',
    'dashboard.builder.step2.axisXTime': 'X axis - Labels / Time',
    'dashboard.builder.step2.axisYValues': 'Y axis - Values',
    'dashboard.builder.step2.categoryField': 'Category field',
    'dashboard.builder.step2.valuesField': 'Values field',
    'dashboard.builder.step2.availableColumns': 'Available columns',
    'dashboard.builder.step2.allAssigned': 'All assigned',
    'dashboard.builder.step2.selectedColumns': 'Selected columns (order)',
    'dashboard.builder.step2.dragHint': 'Drag or click to add',
    'dashboard.builder.step2.direction': 'Direction',
    'dashboard.builder.step2.asc': 'Ascending',
    'dashboard.builder.step2.desc': 'Descending',
    'dashboard.builder.step2.autoMapping': 'Automatic mapping detected',

    'dashboard.builder.step3.title': 'Presentation type',
    'dashboard.builder.step3.subtitle': 'Choose how the indicator will be displayed on the dashboard.',

    'dashboard.builder.step4.title': 'Appearance and size',
    'dashboard.builder.step4.description': 'Short description (optional)',
    'dashboard.builder.step4.preview': 'Preview',
    'dashboard.builder.step4.noRefresh': 'No auto-refresh',
    'dashboard.builder.step4.every30s': 'Every 30 seconds',
    'dashboard.builder.step4.every1m': 'Every minute',
    'dashboard.builder.step4.every2m': 'Every 2 minutes',
    'dashboard.builder.step4.every5m': 'Every 5 minutes',
    'dashboard.builder.step4.every10m': 'Every 10 minutes',
    'dashboard.builder.step4.every30m': 'Every 30 minutes',

    'dashboard.builder.step5.title': 'Publishing and permissions',
    'dashboard.builder.step5.private': 'Private widget (only me)',
    'dashboard.builder.step5.privateHint': 'Only you will see this widget in your personal catalog.',
    'dashboard.builder.step5.public': 'Public widget (corporate)',
    'dashboard.builder.step2.valueFieldLabel': 'Value field',
    'dashboard.builder.step2.detectedValue': 'Value field:',
    'dashboard.builder.step2.detectedDate': 'Date field:',
    'dashboard.builder.step1.searchIndicator': 'Search indicator...',
    'dashboard.builder.step4.namePlaceholder': 'e.g. B2B conversion rate',
    'dashboard.builder.step5.publicIn': 'Public in {alcance}',
    'dashboard.builder.step5.publicInHint':
      'It comes from the «{alcance}» category: it is not asked separately, so the two cannot disagree.',
    'dashboard.builder.step5.noScopeHint':
      'This category does not declare which group it belongs to, so there is nowhere to publish. Pick another category in the previous step.',
    'dashboard.builder.step5.toEveryone': 'To everyone in {alcance}',
    'dashboard.builder.step5.toPeople': 'Only to these people',
    'dashboard.builder.step5.noSearch':
      'This host has no people search. You can share later, from the access dialog.',
    'dashboard.builder.step5.searchPerson': 'Search person…',
    'dashboard.builder.step5.removePerson': 'Remove {persona}',
    'dashboard.builder.step5.noPeopleYet':
      'With nobody on the list, the widget is created restricted and reachable by no one — not even you. Add at least one person, or choose «To everyone in {alcance}».',
    'dashboard.builder.step5.ceilingWarning':
      'It only reaches people who already have {alcance}: the access is still stored for the others, with no effect.',
    'dashboard.builder.step5.partialNobody':
      'Widget «{nombre}» was created and restricted, but access could NOT be granted to anyone: right now nobody can see it, not even you. Open it in the access dialog to share it.',
    'dashboard.builder.step5.partialSome':
      'Widget «{nombre}» was created and restricted. Granted: {ok}. NOT granted: {fallidos}. Open it in the access dialog to finish sharing.',
    'dashboard.builder.step5.partialNotRestricted':
      'Widget «{nombre}» was created, but could NOT be restricted, so it stays private to you and nobody else sees it. Open it in the access dialog to share it.',
    'dashboard.builder.step5.openAccessDialog': 'Open the access dialog',
    'dashboard.builder.step5.dismissPartial': 'Understood, leave it like this for now',
    'dashboard.widget.emptyData': 'No data',
    'dashboard.widget.imageUnavailable': 'Image unavailable',
  },
};

/** Hook de traducción del módulo. Ver la nota de arriba sobre la cascada de resolución. */
export const useDashboardTranslation = createTranslationHook(TRADUCCIONES);

/** Expuesto para tests: permite afirmar que `es` y `en` tienen exactamente las mismas claves. */
export const DASHBOARD_TRANSLATIONS = TRADUCCIONES;
